import React, { useEffect, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, Line, Graticule, Sphere, Annotation } from "react-simple-maps";
import { http } from "@/lib/api";
import CountryDeepDive from "@/components/CountryDeepDive";

const WORLD_TOPO = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const COUNTRY_COORDS = {
  "Canada":         { c: [-96, 60], code: "CA" },
  "United Kingdom": { c: [-2, 54],  code: "GB" },
  "USA":            { c: [-99, 39], code: "US" },
  "Australia":      { c: [134, -25],code: "AU" },
  "Germany":        { c: [10, 51],  code: "DE" },
  "UAE":            { c: [54, 24],  code: "AE" },
  "Singapore":      { c: [104, 1],  code: "SG" },
  "Ireland":        { c: [-8, 53],  code: "IE" },
  "New Zealand":    { c: [172, -41],code: "NZ" },
  "Netherlands":    { c: [5, 52],   code: "NL" },
  "Portugal":       { c: [-8, 39],  code: "PT" },
};

// The 7 featured shores — always surfaced on the map regardless of recorded approvals
const FEATURED = new Set(["Canada", "United Kingdom", "USA", "Australia", "Germany", "UAE", "Singapore"]);

const ORIGINS = [
  { name: "India",       c: [77, 20] },
  { name: "Nigeria",     c: [8,  10] },
  { name: "Brazil",      c: [-55, -10] },
  { name: "China",       c: [116, 35] },
  { name: "Philippines", c: [121, 13] },
];

const CODE_TO_COUNTRY = Object.entries(COUNTRY_COORDS).reduce((acc, [k, v]) => {
  acc[v.code] = k; return acc;
}, {});

export default function WhereNextMap() {
  const [approved, setApproved] = useState([]);
  const [codes, setCodes] = useState([]);
  const [opened, setOpened] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    let ok = true;
    http.get("/approved-countries").then(({ data }) => {
      if (!ok) return;
      setApproved(data.approved || []);
      setCodes(data.testimonial_codes || []);
    }).catch(() => {});
    return () => { ok = false; };
  }, []);

  const counts = new Map();
  approved.forEach((a) => counts.set(a.country, (counts.get(a.country) || 0) + a.count));
  codes.forEach((cc) => {
    const country = CODE_TO_COUNTRY[cc];
    if (country) counts.set(country, (counts.get(country) || 0) + 1);
  });
  // Always surface the 7 featured destinations with at least 1 dot each
  FEATURED.forEach((country) => {
    if (!counts.has(country)) counts.set(country, 1);
  });

  const markers = Array.from(counts.entries())
    .filter(([country]) => FEATURED.has(country))
    .map(([country, count]) => {
      const coord = COUNTRY_COORDS[country];
      return coord ? { country, count, c: coord.c } : null;
    })
    .filter(Boolean);

  const paths = [];
  ORIGINS.forEach((o, oi) => {
    markers.slice(0, 6).forEach((m, mi) => {
      if ((oi + mi) % 2 === 0) paths.push({ from: o.c, to: m.c, key: `${o.name}-${m.country}` });
    });
  });

  const totalCountries = markers.length;
  const totalApprovals = markers.reduce((s, m) => s + m.count, 0);
  const activeMarker = hovered;

  // Smooth continuous compass rotation
  const [rotate, setRotate] = useState([0, 0, 0]);
  const velRef = useRef({ lng: 0, lat: 0 });
  const targetRef = useRef({ lng: 0, lat: 0 });
  const rafRef = useRef(null);
  const rotateRef = useRef([0, 0, 0]);

  const tick = () => {
    const vel = velRef.current;
    const target = targetRef.current;
    // Ease velocity toward target (fast ramp while pressed, decay when released)
    vel.lng += (target.lng - vel.lng) * 0.18;
    vel.lat += (target.lat - vel.lat) * 0.18;

    const r = rotateRef.current;
    const nextLng = r[0] + vel.lng;
    const nextLat = Math.max(-60, Math.min(60, r[1] + vel.lat));
    rotateRef.current = [nextLng, nextLat, 0];
    setRotate(rotateRef.current);

    // Stop when idle and velocity negligible
    if (target.lng === 0 && target.lat === 0 && Math.abs(vel.lng) < 0.01 && Math.abs(vel.lat) < 0.01) {
      vel.lng = 0; vel.lat = 0;
      rafRef.current = null;
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const ensureLoop = () => { if (!rafRef.current) rafRef.current = requestAnimationFrame(tick); };
  const pressDir = (dLng, dLat) => { targetRef.current = { lng: dLng, lat: dLat }; ensureLoop(); };
  const release    = () => { targetRef.current = { lng: 0, lat: 0 }; ensureLoop(); };
  const resetRotate = () => {
    targetRef.current = { lng: 0, lat: 0 };
    velRef.current = { lng: 0, lat: 0 };
    rotateRef.current = [0, 0, 0];
    setRotate([0, 0, 0]);
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // Speed per frame (deg) — ~60fps -> 0.6 * 60 = 36 deg/sec
  const SPEED_LNG = 0.6;
  const SPEED_LAT = 0.45;

  return (
    <section data-testid="where-next-map" className="py-20 bg-app border-t border-[var(--card-border)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-[0.3em] text-[#C9A961] font-semibold">Where next?</span>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-app">
            Approvals across <em className="text-[#C9A961]">seven</em> shores — and growing.
          </h2>
          <p className="text-sm text-app-muted mt-2">
            {totalApprovals ? `${totalApprovals} approvals mapped` : "Every gold dot is a life relocated"} — click a dot to open the destination deep-dive.
          </p>
        </div>

        <div className="relative rounded-3xl overflow-hidden bg-[#0A162B]">
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ scale: 170, rotate }}
            style={{ width: "100%", height: "auto" }}
            data-testid="world-map-svg"
          >
            <defs>
              <radialGradient id="pulseGrad">
                <stop offset="0%" stopColor="#F8E6B0" stopOpacity="1" />
                <stop offset="100%" stopColor="#C9A961" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="oceanGlow" cx="50%" cy="50%" r="55%">
                <stop offset="0%" stopColor="#132446" stopOpacity="1" />
                <stop offset="100%" stopColor="#06101F" stopOpacity="1" />
              </radialGradient>
            </defs>

            {/* Ocean + graticule base */}
            <Sphere stroke="#C9A961" strokeWidth={0.4} strokeOpacity={0.35} fill="url(#oceanGlow)" />
            <Graticule stroke="#C9A961" strokeWidth={0.25} strokeOpacity={0.08} step={[15, 15]} />

            <Geographies geography={WORLD_TOPO}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { fill: "#152742", stroke: "#0B1E3F", strokeWidth: 0.5, outline: "none" },
                      hover:   { fill: "#1E3560", stroke: "#C9A961", strokeWidth: 0.6, outline: "none" },
                      pressed: { fill: "#1E3560", outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {/* Static dim arc base */}
            {paths.map((p) => (
              <Line
                key={`base-${p.key}`}
                from={p.from}
                to={p.to}
                stroke="#C9A961"
                strokeWidth={0.35}
                strokeOpacity={0.18}
                strokeLinecap="round"
                strokeDasharray="1 3"
              />
            ))}

            {/* Primary jumping dot */}
            {paths.map((p, i) => (
              <Line
                key={`jump-${p.key}`}
                from={p.from}
                to={p.to}
                stroke="#F8E6B0"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeDasharray="4 500"
                style={{
                  animation: "nsJump 4.2s cubic-bezier(.6,.1,.4,.9) infinite",
                  animationDelay: `${(i * 0.35) % 4.2}s`,
                  filter: "drop-shadow(0 0 2px #F8E6B0)",
                }}
              />
            ))}

            {/* Trailing secondary dot (staggered) */}
            {paths.map((p, i) => (
              <Line
                key={`jump2-${p.key}`}
                from={p.from}
                to={p.to}
                stroke="#C9A961"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeDasharray="3 500"
                strokeOpacity={0.75}
                style={{
                  animation: "nsJump 4.2s cubic-bezier(.6,.1,.4,.9) infinite",
                  animationDelay: `${(i * 0.35 + 1.5) % 4.2}s`,
                }}
              />
            ))}

            {/* Tertiary tracer */}
            {paths.map((p, i) => (
              <Line
                key={`jump3-${p.key}`}
                from={p.from}
                to={p.to}
                stroke="#F8E6B0"
                strokeWidth={0.8}
                strokeLinecap="round"
                strokeDasharray="2 500"
                strokeOpacity={0.45}
                style={{
                  animation: "nsJump 4.2s cubic-bezier(.6,.1,.4,.9) infinite",
                  animationDelay: `${(i * 0.35 + 2.8) % 4.2}s`,
                }}
              />
            ))}

            {/* Origin pulses */}
            {ORIGINS.map((o) => (
              <Marker key={o.name} coordinates={o.c} data-testid={`origin-${o.name.toLowerCase()}`}>
                <circle r={9} fill="url(#pulseGrad)">
                  <animate attributeName="r" values="4;11;4" dur="2.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0.2;0.9" dur="2.8s" repeatCount="indefinite" />
                </circle>
                <circle r={2.4} fill="#F8E6B0" fillOpacity={0.95} />
                <text y={-10} textAnchor="middle" style={{ fill: "#F8E6B0", fontSize: 6.5, fontFamily: "system-ui", letterSpacing: 1, textTransform: "uppercase", opacity: 0.85 }}>
                  {o.name}
                </text>
              </Marker>
            ))}

            {/* Destination markers with always-on count badge */}
            {markers.map((m) => {
              const size = Math.min(20, 6 + m.count * 1.4);
              const isActive = activeMarker?.country === m.country || opened?.country === m.country;
              return (
                <Marker
                  key={m.country}
                  coordinates={m.c}
                  onClick={() => setOpened(m)}
                  onMouseEnter={() => setHovered(m)}
                  onMouseLeave={() => setHovered(null)}
                  data-testid={`map-marker-${m.country.replace(/\s/g, "-").toLowerCase()}`}
                  style={{ cursor: "pointer" }}
                >
                  <circle r={size * 1.4} fill="#C9A961" fillOpacity={0.08}>
                    <animate attributeName="r" values={`${size * 1.1};${size * 1.8};${size * 1.1}`} dur="3s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.12;0.02;0.12" dur="3s" repeatCount="indefinite" />
                  </circle>
                  <circle r={size} fill="#C9A961" fillOpacity={isActive ? 0.5 : 0.28} />
                  <circle r={size / 2.2} fill="#C9A961">
                    <animate attributeName="r" values={`${size / 2.2};${size / 1.6};${size / 2.2}`} dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  {/* Count badge */}
                  <g transform={`translate(${size + 2},-${size - 1})`} style={{ pointerEvents: "none" }}>
                    <rect x={0} y={-6} rx={5} ry={5} width={18} height={11} fill="#0B1E3F" stroke="#C9A961" strokeWidth={0.5} />
                    <text x={9} y={2} textAnchor="middle" style={{ fill: "#F8E6B0", fontSize: 7, fontFamily: "system-ui", fontWeight: 700 }}>
                      {m.count}
                    </text>
                  </g>
                </Marker>
              );
            })}

            {/* Annotation callouts for a cartographic feel */}
            {markers.slice(0, 3).map((m, i) => (
              <Annotation
                key={`anno-${m.country}`}
                subject={m.c}
                dx={m.c[0] > 0 ? 24 : -24}
                dy={-18 - i * 6}
                connectorProps={{ stroke: "#C9A961", strokeWidth: 0.5, strokeOpacity: 0.5, strokeLinecap: "round" }}
              >
                <text
                  x={m.c[0] > 0 ? 4 : -4}
                  textAnchor={m.c[0] > 0 ? "start" : "end"}
                  style={{ fill: "#F8E6B0", fontSize: 6.5, fontFamily: "system-ui", letterSpacing: 1, textTransform: "uppercase", opacity: 0.9 }}
                >
                  {m.country}
                </text>
              </Annotation>
            ))}

            {activeMarker && (() => {
              const size = Math.min(20, 6 + activeMarker.count * 1.4);
              return (
                <Marker coordinates={activeMarker.c}>
                  <g transform={`translate(0,-${20 + size})`} style={{ pointerEvents: "none" }}>
                    <rect x={-56} y={-28} rx={6} ry={6} width={112} height={28} fill="#0B1E3F" stroke="#C9A961" strokeWidth={0.8} />
                    <text textAnchor="middle" y={-15} style={{ fill: "#F8E6B0", fontSize: 9, fontFamily: "serif", fontWeight: 700 }}>
                      {activeMarker.country}
                    </text>
                    <text textAnchor="middle" y={-4} style={{ fill: "#C9A961", fontSize: 7.2, fontFamily: "system-ui", letterSpacing: 0.5 }}>
                      Click for Work · Study · PR
                    </text>
                    <polygon points="-5,0 5,0 0,5" fill="#0B1E3F" stroke="#C9A961" strokeWidth={0.6} />
                  </g>
                </Marker>
              );
            })()}
          </ComposableMap>

          <div className="absolute left-4 top-4 bg-[#0B1E3F]/85 border border-[#C9A961]/30 rounded-xl px-4 py-3 text-white backdrop-blur-md">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961]">Live tally</div>
            <div className="font-serif text-2xl">{totalApprovals || 0}</div>
            <div className="text-[10px] text-slate-300">across {totalCountries || 0} countries</div>
          </div>

          <div className="absolute right-4 bottom-4 bg-[#0B1E3F]/85 border border-[#C9A961]/30 rounded-xl px-3 py-2 text-white backdrop-blur-md text-[10px] uppercase tracking-widest flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#F8E6B0] shadow-[0_0_6px_#F8E6B0]"></span> Origin
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#C9A961]"></span> Destination
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-[#F8E6B0]"></span> Case in motion
            </span>
          </div>

          {/* Interactive compass rose — pans the globe */}
          <div className="absolute right-4 top-4 w-16 h-16 select-none">
            <svg viewBox="0 0 60 60" className="w-full h-full">
              <circle cx="30" cy="30" r="26" fill="#0B1E3F" fillOpacity="0.6" stroke="#C9A961" strokeOpacity="0.5" strokeWidth="0.7" />
              <circle cx="30" cy="30" r="18" fill="none" stroke="#C9A961" strokeOpacity="0.25" strokeWidth="0.4" />
              <polygon points="30,6 32,30 30,30" fill="#F8E6B0" />
              <polygon points="30,54 28,30 30,30" fill="#C9A961" fillOpacity="0.65" />
              <polygon points="6,30 30,28 30,30" fill="#C9A961" fillOpacity="0.4" />
              <polygon points="54,30 30,32 30,30" fill="#C9A961" fillOpacity="0.4" />
              <text x="30" y="4"  textAnchor="middle" fill="#F8E6B0" fontSize="5" fontFamily="serif" fontWeight="700">N</text>
              <text x="30" y="58" textAnchor="middle" fill="#C9A961" fontSize="5" fontFamily="serif">S</text>
              <text x="2"  y="31" textAnchor="middle" fill="#C9A961" fontSize="5" fontFamily="serif">W</text>
              <text x="58" y="31" textAnchor="middle" fill="#C9A961" fontSize="5" fontFamily="serif">E</text>

              {/* clickable quadrant hitboxes — press-and-hold for smooth rotation */}
              <path d="M30 30 L8 8 A32 32 0 0 1 52 8 Z" fill="transparent" style={{ cursor: "pointer" }}
                    onPointerDown={() => pressDir(0, -SPEED_LAT)} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
                    data-testid="compass-n"><title>Hold to pan North</title></path>
              <path d="M30 30 L52 52 A32 32 0 0 1 8 52 Z" fill="transparent" style={{ cursor: "pointer" }}
                    onPointerDown={() => pressDir(0, SPEED_LAT)} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
                    data-testid="compass-s"><title>Hold to pan South</title></path>
              <path d="M30 30 L52 8 A32 32 0 0 1 52 52 Z" fill="transparent" style={{ cursor: "pointer" }}
                    onPointerDown={() => pressDir(-SPEED_LNG, 0)} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
                    data-testid="compass-e"><title>Hold to pan East</title></path>
              <path d="M30 30 L8 52 A32 32 0 0 1 8 8 Z" fill="transparent" style={{ cursor: "pointer" }}
                    onPointerDown={() => pressDir(SPEED_LNG, 0)} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
                    data-testid="compass-w"><title>Hold to pan West</title></path>

              {/* reset hub */}
              <circle cx="30" cy="30" r="4" fill="#C9A961" style={{ cursor: "pointer" }}
                      onClick={resetRotate} data-testid="compass-reset"><title>Reset view</title></circle>
            </svg>
          </div>

          {/* Decorative corner frame */}
          <div className="absolute left-3 bottom-3 pointer-events-none">
            <svg width="56" height="56" viewBox="0 0 56 56">
              <path d="M2 54 L2 20 L20 2 L54 2" fill="none" stroke="#C9A961" strokeOpacity="0.3" strokeWidth="0.8" strokeLinecap="round" />
              <circle cx="2" cy="54" r="1.5" fill="#C9A961" />
              <circle cx="54" cy="2" r="1.5" fill="#C9A961" />
            </svg>
          </div>
        </div>
      </div>

      {opened && (
        <CountryDeepDive country={opened.country} count={opened.count} onClose={() => setOpened(null)} />
      )}

      <style>{`
        @keyframes nsJump {
          0%   { stroke-dashoffset: 500; opacity: 0; }
          8%   { opacity: 1; }
          85%  { opacity: 1; }
          100% { stroke-dashoffset: 0;   opacity: 0; }
        }
      `}</style>
    </section>
  );
}
